// Pure polygon area helpers used by the JobService mixin (jobService.js).
//
// Extracted verbatim from the mixin so the area math can be unit-tested with
// Jest. The formula assumes GeoJSON positions in lon/lat degrees (EPSG:4326);
// projected coordinates (e.g. EPSG:3857 meters) must be reprojected first.

export function rad(_) {
  return _ * Math.PI / 180;
}

/**
 * Calculate the approximate area of the polygon were it projected onto
 *     the earth.  Note that this area will be positive if ring is oriented
 *     clockwise, otherwise it will be negative.
 *
 * Reference:
 * Robert. G. Chamberlain and William H. Duquette, "Some Algorithms for
 *     Polygons on a Sphere", JPL Publication 07-03, Jet Propulsion
 *     Laboratory, Pasadena, CA, June 2007 http://trs-new.jpl.nasa.gov/dspace/handle/2014/40409
 *
 * Returns:
 * {float} The approximate signed geodesic area of the polygon in square
 *     meters.
 */
export function ringArea(coords) {
  var p1, p2, p3, lowerIndex, middleIndex, upperIndex, i,
  area = 0,
  coordsLength = coords.length;
  var wgs84_RADIUS = 6378137

  if (coordsLength > 2) {
    for (i = 0; i < coordsLength; i++) {
      if (i === coordsLength - 2) {// i = N-2
        lowerIndex = coordsLength - 2;
        middleIndex = coordsLength -1;
        upperIndex = 0;
      } else if (i === coordsLength - 1) {// i = N-1
        lowerIndex = coordsLength - 1;
        middleIndex = 0;
        upperIndex = 1;
      } else { // i = 0 to N-3
        lowerIndex = i;
        middleIndex = i+1;
        upperIndex = i+2;
      }
      p1 = coords[lowerIndex];
      p2 = coords[middleIndex];
      p3 = coords[upperIndex];
      area += ( rad(p3[0]) - rad(p1[0]) ) * Math.sin( rad(p2[1]) );
    }

    area = area * wgs84_RADIUS * wgs84_RADIUS / 2;
  }

  return area;
}

// Area of a Polygon's coordinates (outer ring minus holes), in square feet.
export function polygonArea(coords) {
  var area = 0;
  if (coords && coords.length > 0) {
    area += Math.abs(ringArea(coords[0]));
    for (var i = 1; i < coords.length; i++) {
      area -= Math.abs(ringArea(coords[i]));
    }
  }
  return area * 10.7639; // Convert from square meters to square feet
}

// Area of any GeoJSON geometry, in square feet. Non-areal, null or unknown
// geometries count as 0.
export function geometryArea(_) {
  var area = 0, i;
  if (!_) return 0;
  switch (_.type) {
    case 'Polygon':
      return polygonArea(_.coordinates);
    case 'MultiPolygon':
      for (i = 0; i < _.coordinates.length; i++) {
        area += polygonArea(_.coordinates[i]);
      }
      return area;
    case 'Point':
    case 'MultiPoint':
    case 'LineString':
    case 'MultiLineString':
      return 0;
    case 'GeometryCollection':
      for (i = 0; i < _.geometries.length; i++) {
        area += geometryArea(_.geometries[i]);
      }
      return area;
    default:
      return 0;
  }
}

// Job size class for an area in square feet (drives the Timeframe limits).
export function jobSizeClassForArea(area_sqft) {
  let jobSizeClass = null;
  if (area_sqft < 20000000) { // less than 20 million sqft
    jobSizeClass = "Small";
  } else if (area_sqft < (200 * 1000000)) { // less than 200 million sqft
    jobSizeClass = "Medium";
  } else if (area_sqft < (500 * 1000000)) { // less than 500 million sqft
    jobSizeClass = "Large";
  } else if (area_sqft < (10 * 1000 * 1000000)) { // less than 10 billion sqft
    jobSizeClass = "xLarge";
  } else {
    jobSizeClass = "xxLarge";
  }
  return jobSizeClass;
}
